import CompleteRecording from '@Application/Beneficiaries/Interviews/Commands/CompleteRecording';
import TransferRecordingSegment from '@Application/Beneficiaries/Interviews/Commands/TransferRecordingSegment';
import completeRecording from '@Application/Beneficiaries/Interviews/completeRecording';
import probeBeneficiaryAccess from '@Application/Beneficiaries/Interviews/probeBeneficiaryAccess';
import ensureServerInterview from '@Application/Beneficiaries/Interviews/ensureServerInterview';
import fetchRecordingSegments from '@Application/Beneficiaries/Interviews/fetchRecordingSegments';
import FetchRecordingSegments from '@Application/Beneficiaries/Interviews/Queries/FetchRecordingSegments';
import recordingTransferStarted from '@Application/Beneficiaries/Interviews/Actions/recordingTransferStarted';
import transferRecordingSegment from '@Application/Beneficiaries/Interviews/transferRecordingSegment';
import readSubjectAddressFromContext from '@Application/Subjects/readSubjectAddressFromContext';
import type { LocalInterviewPort } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type {
	TransferHandler,
	TransferOutcome,
	VerifiedSegmentDigest,
} from '@Domain/Ports/Outbox/OutboxPort';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ApiError from '@Infra/FetchLib/ApiError';
import type { ExtractedMessages } from '@Infra/FetchLib/ApiError/extractMessages';
import type { ActionAsFunction, DefaultPayload } from '@verclam/synapse';

const RETRY_FALLBACK_MESSAGE = "Le transfert n'a pas abouti.";
const FAILED_FALLBACK_MESSAGE = 'Le transfert a échoué et ne sera pas relancé automatiquement.';
const EMPTY_RECORDING_MESSAGE = "Aucun son n'a été capté pendant cet enregistrement.";
const INVALID_CONTEXT_MESSAGE = "Le contexte de l'enregistrement est invalide.";
/**
 * Distinct from `INVALID_CONTEXT_MESSAGE`: an unreadable context and a well-formed context pointing
 * at an interview this device no longer holds are different facts, and the difference is exactly
 * what orients a diagnosis, since `reason` is displayed to the user as-is.
 */
const MISSING_LOCAL_INTERVIEW_MESSAGE = "Cet enregistrement n'existe plus sur cet appareil.";
const DELETED_INTERVIEW_MESSAGE = 'Cet enregistrement a été supprimé du dossier.';
const INACCESSIBLE_SUBJECT_MESSAGE =
	"Le dossier de cet enregistrement n'est plus accessible depuis votre compte.";

/**
 * The `dispatch` `useDispatch()` hands out: it only ever accepts a thunk, never a bare action —
 * imported from the library's own types (`import type`, erased at compile time, so `Application/`
 * still never depends on `@verclam/synapse` at runtime) rather than redeclared, since a weaker
 * hand-written alias would not be assignable from the real `useDispatch()` return value.
 */
type TransferDispatch = <A extends DefaultPayload>(thunk: ActionAsFunction<A>) => Promise<A>;

/** 401 is retried once by `Domain/Api.ts` itself; 408/429/5xx and network failures are transient. */
const isRetryableStatus = (status: number): boolean => {
	return status === 401 || status === 408 || status === 429 || status >= 500;
};

/**
 * The shape shared by every failure action this handler's dependencies dispatch: `status` is read
 * straight off the HTTP response by the applicative function that dispatched it, never derived from
 * `extractMessages`, which answers `undefined` for a bodiless error — indistinguishable from a
 * network failure and unusable to decide a retry.
 */
type TransferFailureAction = {
	readonly type: string;
	readonly payload: ExtractedMessages;
	readonly status: number | null;
};

const isTransferFailureAction = (candidate: unknown): candidate is TransferFailureAction => {
	if (typeof candidate !== 'object' || candidate === null) {
		return false;
	}

	return 'type' in candidate && 'status' in candidate && 'payload' in candidate;
};

const buildOutcomeFromStatus = (status: number | null, reason: string | undefined): TransferOutcome => {
	if (status === null || isRetryableStatus(status)) {
		return { status: 'retry', reason: reason ?? RETRY_FALLBACK_MESSAGE };
	}

	return { status: 'failed', reason: reason ?? FAILED_FALLBACK_MESSAGE };
};

/**
 * Translates a rejected creation, transfer or finalisation dispatch into a `TransferOutcome`. Stays
 * total on three forms: a failure action dispatched by `createInterview`/`transferRecordingSegment`/
 * `completeRecording`, a raw `ApiError` (a call that never went through `dispatch`), and a bare
 * `Error` (a network failure or anything else) — the last two never carry a usable `status`, so
 * `null` reads as `retry`, exactly like a network failure always has. The `reason` is always French
 * and displayable as-is (`OutboxPort.ts`, `lastErrorMessage`) — never a raw `statusText`.
 */
const toTransferOutcome = (error: unknown): TransferOutcome => {
	if (isTransferFailureAction(error)) {
		return buildOutcomeFromStatus(error.status, error.payload?.message);
	}

	if (error instanceof ApiError) {
		return buildOutcomeFromStatus(error.status, error.errorMessage);
	}

	return { status: 'retry', reason: RETRY_FALLBACK_MESSAGE };
};

const readFailureStatus = (error: unknown): number | null => {
	if (isTransferFailureAction(error)) {
		return error.status;
	}

	return error instanceof ApiError ? error.status : null;
};

/**
 * Same as `toTransferOutcome`, for a call made on an interview the server knows: a 404 there means
 * either the interview was deleted or the subject file left this account's reach (a 404 also
 * comes from an unassigned file), so the file is read once to tell them apart. A failed read says
 * nothing and is retried.
 */
const toOutcomeForKnownInterview = async (
	error: unknown,
	facilityId: number,
	subject: SubjectAddress,
): Promise<TransferOutcome> => {
	if (readFailureStatus(error) !== 404) {
		return toTransferOutcome(error);
	}

	try {
		const access = await probeBeneficiaryAccess(facilityId, subject);

		return access === 'accessible'
			? { status: 'orphaned', reason: DELETED_INTERVIEW_MESSAGE }
			: { status: 'failed', reason: INACCESSIBLE_SUBJECT_MESSAGE };
	} catch {
		return { status: 'retry', reason: RETRY_FALLBACK_MESSAGE };
	}
};

/**
 * Dispatches an already-built, synchronous action through the outer `dispatch` this handler
 * receives. That `dispatch` only accepts a thunk (`ActionAsFunction<A>`), never a bare action, so a
 * plain fact — here, `RECORDING_TRANSFER_STARTED` — is wrapped in the smallest thunk that reports it
 * to the inner, raw dispatcher.
 */
const dispatchPlainAction = <A extends DefaultPayload>(dispatch: TransferDispatch, action: A): Promise<A> => {
	return dispatch(async (rawDispatch) => {
		rawDispatch(action);
		return action;
	});
};

/** `item.context` values are `string | number` by port contract: read defensively, never with `any`. */
const readNumericContextValue = (value: string | number | undefined): number => {
	if (value === undefined) {
		throw new Error(INVALID_CONTEXT_MESSAGE);
	}

	const numericValue = typeof value === 'number' ? value : Number(value);

	if (Number.isNaN(numericValue)) {
		throw new Error(INVALID_CONTEXT_MESSAGE);
	}

	return numericValue;
};

/** Reads the subject address of the context, current or legacy form. */
const readSubjectContextValue = (context: Readonly<Record<string, string | number>>): SubjectAddress => {
	const subject = readSubjectAddressFromContext(context);

	if (subject === null) {
		throw new Error(INVALID_CONTEXT_MESSAGE);
	}

	return subject;
};

/**
 * Reads `item.context.localInterviewId`: a UUID is not a number, and `readNumericContextValue` would
 * coerce it to `NaN` instead of reporting it as invalid.
 */
const readStringContextValue = (value: string | number | undefined): string => {
	if (typeof value !== 'string') {
		throw new Error(INVALID_CONTEXT_MESSAGE);
	}

	return value;
};

/**
 * Builds the `TransferHandler` for the `'interview-recording'` outbox kind — the sole reason `UI/`
 * never needs to import `Infra/` directly. `dispatch` is `useDispatch()`'s own function, supplied by
 * the caller (`RecordingTransferHost`) — every server call this handler makes runs through it, so
 * the store reflects the transfer. `localInterviewPort` is likewise supplied by the caller, never
 * resolved here, so this factory stays a pure function of its arguments. When the local interview behind this item has not yet been created on the server
 * (`interviewId === null`), the handler issues the composite creation call and writes the server
 * identity back to the local interview before a single byte is sent; otherwise it moves straight to
 * segment transfer. Segments are sent one `PUT` per segment: `concurrencyFetch`
 * (`Infra/FetchLib/fetch.ts`) memoizes by `JSON.stringify([url, init])`, which collapses any
 * `FormData` body to `{}`, so a shared URL across segments would silently return the same response to
 * every call. `position` is renamed to `segment` because the wire and the port name the same value
 * differently — an omission here fails the integrity check closed (bytes kept, transfer never
 * completes) rather than open. Segments are never renumbered: the port pairs digests by rank, and a
 * sparse rank sequence (an interruption recovered mid-capture) is a nominal outcome, not a gap to fix.
 * Deletion is never performed here — only the port's own integrity barrier, run after this handler
 * returns, may erase the local buffer.
 *
 * `RECORDING_TRANSFER_STARTED` is dispatched exactly once per call to this handler — once per
 * transfer attempt, never once per segment, and never deduplicated across a retried attempt, since
 * the transfer genuinely restarted. It carries `segmentsToTransfer`, the size of this attempt's
 * whole batch, never the remainder after a resume skip.
 *
 * Resume: when `item.integrityFailureCount === 0`, the handler first reads the segments the server
 * already holds (`fetchRecordingSegments`) and skips re-sending any local segment whose position is
 * already there, pushing the *server's* digest for it straight into `verifiedDigests` — no bytes are
 * ever re-sent for it, and no locally-computed digest is ever substituted. Once
 * `integrityFailureCount` is greater than zero, resume is disabled for good on this item and every
 * segment is re-sent: the handler cannot tell "already there, identical" from "already there,
 * different" (`TransferSegment` never carries the local digest), so a diverging position must be
 * healed by a full retransmission rather than skipped again. A failure of the read itself never
 * fails the transfer: it degrades the optimisation, and every segment is sent as if nothing had ever
 * been received.
 */
const createRecordingTransferHandler = (
	dispatch: TransferDispatch,
	localInterviewPort: LocalInterviewPort,
): TransferHandler => {
	return async ({ item, segments, report, signal }) => {
		if (segments.length === 0) {
			return { status: 'failed', reason: EMPTY_RECORDING_MESSAGE };
		}

		let facilityId: number;
		let subject: SubjectAddress;
		let localInterviewId: string;

		try {
			facilityId = readNumericContextValue(item.context.facilityId);
			subject = readSubjectContextValue(item.context);
			localInterviewId = readStringContextValue(item.context.localInterviewId);
		} catch (error) {
			return {
				status: 'failed',
				reason: error instanceof Error ? error.message : INVALID_CONTEXT_MESSAGE,
			};
		}

		const localInterview = await localInterviewPort.get(localInterviewId);

		if (localInterview === null) {
			return { status: 'failed', reason: MISSING_LOCAL_INTERVIEW_MESSAGE };
		}

		/**
		 * `localInterviewPort.get()` is deliberately not filtered by owner (the device is shared): the
		 * caller that needs the ownership guarantee checks it itself. The two records are minted in the
		 * same session in practice, so this never fires today — but its absence would, the day a
		 * shortcut changes that, leak an interview across accounts on the same device. Reusing the
		 * "missing" message keeps the response from confirming, to a caller acting on another owner's
		 * behalf, that the interview exists at all.
		 */
		if (localInterview.ownerUserId !== item.ownerUserId) {
			return { status: 'failed', reason: MISSING_LOCAL_INTERVIEW_MESSAGE };
		}

		let identity: Awaited<ReturnType<typeof ensureServerInterview>>;

		try {
			identity = await ensureServerInterview(dispatch, localInterviewPort, localInterview);
		} catch (error) {
			return toTransferOutcome(error);
		}

		if (identity.type === 'invalid-context') {
			return { status: 'failed', reason: INVALID_CONTEXT_MESSAGE };
		}

		if (identity.type === 'missing-recording') {
			return { status: 'failed', reason: FAILED_FALLBACK_MESSAGE };
		}

		if (identity.type === 'rejected') {
			return toTransferOutcome(identity.action);
		}

		const { interviewId, recordingId } = identity;

		const verifiedDigests: VerifiedSegmentDigest[] = [];
		const alreadyStoredDigestsByPosition = new Map<number, string>();

		if (item.integrityFailureCount === 0) {
			try {
				const action = await dispatch(
					fetchRecordingSegments(
						new FetchRecordingSegments(facilityId, subject, interviewId, recordingId),
					),
				);

				if (action.type !== 'RECORDING_SEGMENTS_FETCHED') {
					// Unreachable in practice: a failed fetch rejects the dispatch instead, and the catch
					// below discards it — the resume read never fails the transfer either way.
					throw new Error(RETRY_FALLBACK_MESSAGE);
				}

				for (const knownSegment of action.payload) {
					alreadyStoredDigestsByPosition.set(knownSegment.position, knownSegment.digest);
				}
			} catch {
				// The resume read is an optimisation only — its failure never fails the transfer, and
				// every segment below is sent as if nothing had ever been received. Catches a rejected
				// `RECORDING_SEGMENTS_FETCH_FAILED` action dispatched by `fetchRecordingSegments`.
			}
		}

		try {
			await dispatchPlainAction(
				dispatch,
				recordingTransferStarted(
					{ segmentsToTransfer: segments.length },
					interviewId,
					recordingId,
					subject.subjectId,
				),
			);

			for (const [index, segment] of segments.entries()) {
				if (signal.aborted) {
					return { status: 'retry', reason: RETRY_FALLBACK_MESSAGE };
				}

				const alreadyStoredDigest = alreadyStoredDigestsByPosition.get(segment.segment);

				if (alreadyStoredDigest !== undefined) {
					verifiedDigests.push({ segment: segment.segment, digest: alreadyStoredDigest });
					report({ phase: 'transferring', progress: (index + 1) / segments.length });
					continue;
				}

				const action = await dispatch(
					transferRecordingSegment(
						new TransferRecordingSegment(
							facilityId,
							subject,
							interviewId,
							recordingId,
							segment.segment,
							segment.blob,
						),
					),
				);

				if (action.type !== 'RECORDING_SEGMENT_TRANSFERRED') {
					return toOutcomeForKnownInterview(action, facilityId, subject);
				}

				const response = action.payload;
				verifiedDigests.push({ segment: response.position, digest: response.digest });

				report({ phase: 'transferring', progress: (index + 1) / segments.length });
			}

			const completionAction = await dispatch(
				completeRecording(
					new CompleteRecording(
						facilityId,
						subject,
						interviewId,
						recordingId,
						item.durationMs,
						segments.length,
						item.mimeType,
					),
				),
			);

			if (completionAction.type !== 'RECORDING_COMPLETED') {
				return toOutcomeForKnownInterview(completionAction, facilityId, subject);
			}

			return { status: 'confirmed', verifiedDigests };
		} catch (error) {
			return toOutcomeForKnownInterview(error, facilityId, subject);
		}
	};
};

export default createRecordingTransferHandler;
